import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown'; 
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faChild, 
  faRobot, 
  faImage, 
  faDownload, 
  faUpload,
  faTimes,
  faMicrophone,
  faGlobe,
  faChevronDown
} from '@fortawesome/free-solid-svg-icons';
import { downloadImage } from '../utils/imageUtils'; 
import styles from './Chat.module.css';
import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition';
import useChat from '../hooks/useChat';
import { languageService } from '../services/language-service';
import uiLocalization from '../services/ui-localization';

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

function Chat({ onLanguageChange }) {
  // Use the hook instead of local state
  const { messages, isLoading, sendMessage, generateImage, changeLanguage } = useChat();
  
  const [input, setInput] = useState('');
  const [pastedImage, setPastedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState(languageService.getLanguage());
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);
  const messagesEndRef = useRef(null);
  const dropAreaRef = useRef(null);
  const inputRef = useRef(null);
  const languageMenuRef = useRef(null);

  // Helper function to check if input is asking about capabilities
  const isCapabilityQuery = (input) => {
    if (!input) return false;
    
    const normalizedInput = input.toLowerCase().trim().replace(/\?$/, '');
    
    // Common English capability queries
    const englishQueries = [
      'what can you do',
      'what are your capabilities',
      'how can you help',
      'help',
      'info',
      'capabilities',
      'features',
      'commands',
      'options',
      'functions',
      'instructions',
      'how to use',
      'how do i use',
      'how does this work',
      'guide',
      'tutorial'
    ];
    
    // Check English queries first
    if (englishQueries.some(query => normalizedInput.includes(query))) {
      return true;
    }
    
    // Language-specific capability queries
    const helpCommands = {
      es: ['ayuda', 'que puedes hacer', 'como funciona', 'instrucciones'],
      fr: ['aide', 'que peux-tu faire', 'comment ça marche', 'instructions'],
      ja: ['ヘルプ', '何ができる', '使い方', '説明'],
      it: ['aiuto', 'cosa puoi fare', 'come funziona', 'istruzioni'],
      de: ['hilfe', 'was kannst du', 'wie funktioniert', 'anleitung'],
      ro: ['ajutor', 'ce poți face', 'cum funcționează', 'instrucțiuni'],
      pt: ['ajuda', 'o que você pode fazer', 'como funciona', 'instruções'],
      ru: ['помощь', 'что ты умеешь', 'как это работает', 'инструкции'],
      ko: ['도움말', '무엇을 할 수 있나요', '어떻게 작동하나요', '설명서'],
      zh: ['帮助', '你能做什么', '怎么用', '说明'],
      ar: ['مساعدة', 'ماذا يمكنك أن تفعل', 'كيف يعمل هذا', 'تعليمات'],
      hi: ['मदद', 'आप क्या कर सकते हैं', 'यह कैसे काम करता है', 'निर्देश'],
      da: ['hjælp', 'hvad kan du', 'hvordan virker det', 'instruktioner']
    };
    
    // Get current language
    const langCode = currentLanguage.split('-')[0];
    
    // Check if the input matches any help command in the current language
    if (helpCommands[langCode]) {
      return helpCommands[langCode].some(cmd => normalizedInput.includes(cmd));
    }
    
    return false;
  };

  // Track if this is the first load
  const isInitialRender = useRef(true);

  // Scroll to bottom only when messages change (not on initial load)
  useEffect(() => {
    // Skip scrolling on first render
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }
    
    // Only scroll when messages are added after initial render
    if (messages.length > 0) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  // Prevent any initial scrolling when component mounts
  useEffect(() => {
    // Reset scroll position to top on initial render
    if (isInitialRender.current) {
      window.scrollTo(0, 0);
    }
  }, []);

  // --- Speech Recognition Setup ---
  const { 
    transcript,
    listening,
    resetTranscript,
    browserSupportsSpeechRecognition
    // Removing unused variable to fix ESLint warning
  } = useSpeechRecognition({
    // Configuration options 
    commands: [] // We don't need command detection as we're handling everything manually
  });
  
  // Track the last time transcript was updated to detect pauses
  const [lastTranscriptUpdate, setLastTranscriptUpdate] = useState(Date.now());
  // Track whether we're waiting to auto-submit after a pause
  const [isListeningForPause, setIsListeningForPause] = useState(false);
  // Configure the pause duration (2 seconds of silence before sending)
  const PAUSE_THRESHOLD_MS = 2000;

  // Update input field with transcript when listening
  useEffect(() => {
    if (listening) {
      setInput(transcript);
      // Update the timestamp for the last transcript change
      setLastTranscriptUpdate(Date.now());
      // If we got new transcript, ensure listening for pause is active
      setIsListeningForPause(true);
    }
  }, [transcript, listening]);
  
  // Auto-submit logic when speech pauses
  useEffect(() => {
    let autoSubmitTimer;
    
    // Only set up auto-submit when we're actively listening and have some transcript
    if (listening && isListeningForPause && transcript.trim()) {
      autoSubmitTimer = setInterval(() => {
        const timeSinceLastUpdate = Date.now() - lastTranscriptUpdate;
        
        // If we detect a pause of sufficient length and have transcript content
        if (timeSinceLastUpdate > PAUSE_THRESHOLD_MS && transcript.trim()) {
          console.log(`[Speech] Detected ${timeSinceLastUpdate}ms pause, auto-submitting`);
          
          // Clear listening state first
          setIsListeningForPause(false);
          clearInterval(autoSubmitTimer);
          
          // Stop the speech recognition
          SpeechRecognition.stopListening();
          
          // Submit the form with the current transcript
          // Using transcript directly instead of creating an unused variable
          handleSubmit({
            preventDefault: () => {}, // Mock the event object
            autoSubmitted: true // Flag to indicate auto-submission
          });
        }
      }, 500); // Check every 500ms to avoid excessive checks
    }
    
    return () => {
      clearInterval(autoSubmitTimer);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listening, transcript, lastTranscriptUpdate, isListeningForPause]);
  // Note: handleSubmit is intentionally omitted from deps to avoid recreation/infinite loops
  // -----------------------------

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Don't submit if there's no input text and no image
    if (!input.trim() && !pastedImage) return;
    
    // If this was auto-submitted from voice input, log it
    if (e.autoSubmitted) {
      console.log('[Chat] Auto-submitting voice input:', input);  
    }

    console.log('Handling submit with input:', input || '(no text)');
    console.log('Pasted image present:', !!pastedImage);
    
    // Initialize audio context on user interaction
    initializeAudioContext();

    const currentInput = input.trim(); // Use trimmed input
    const currentPastedImage = pastedImage;

    // Reset input fields immediately
    setInput('');
    setPastedImage(null);
    setImagePreview(null);

    // Stop listening if voice input was used
    if (listening) {
      SpeechRecognition.stopListening();
      resetTranscript();
    }

    try {
      // Check for capability query first using our comprehensive multilingual check
      if (isCapabilityQuery(currentInput)) {
        // For capability queries, we'll handle locally
        // This will be outside the hook's control
        sendMessage({ text: currentInput });
        return; // Stop processing, we handled it locally
      }

      // Check for image generation commands in current language or English
      const imageCommands = uiLocalization.getLocalizedText('generateImageCommand', null, currentLanguage);
      const localizedImageCommands = Array.isArray(imageCommands) ? imageCommands : [imageCommands];
      
      // Determine if this is an image generation command in any supported language
      let matchedCommand = null;
      const isExplicitImageGeneration = localizedImageCommands.some(command => {
        // More flexible pattern to match command followed by any separator (space, colon, etc.)
        // This helps with RTL languages like Arabic where users might use different separators
        /* eslint-disable-next-line no-useless-escape */
        const commandRegex = new RegExp(`^${escapeRegExp(command)}\\s*[:\\uff1a]?\\s*`, 'i');  // \s is for whitespace
        
        console.log(`Testing command: ${command} against input: ${currentInput}`);
        
        if (commandRegex.test(currentInput)) {
          console.log(`Command ${command} matched!`);
          matchedCommand = command;
          return true;
        }
        return false;
      });

      if (isExplicitImageGeneration && matchedCommand) {
        // --- Explicit Image Generation Request ---
        // Extract the prompt after the matched command, accounting for any separator
        // eslint-disable-next-line no-useless-escape
        const prompt = currentInput.replace(new RegExp(`^${escapeRegExp(matchedCommand)}\\s*[:\\uff1a]?\\s*`, 'i'), '').trim();
        console.log(`Extracted prompt: "${prompt}" from input: "${currentInput}"`);
        console.log('Sending image generation request with prompt:', prompt);

        // Use the hook's generateImage function
        await generateImage(prompt || 'Generate an image');
      } else {
        // --- Text Chat Request (potentially with an image) ---
        console.log('Sending chat completion request (text/image):', currentInput);

        // Use the hook's sendMessage function
        await sendMessage({ 
          text: currentInput, 
          imageBase64: currentPastedImage 
        });
      }
    } catch (error) {
      console.error('Error in handleSubmit:', error);
      // Error handling is now done in the hook
    } finally {
      setPastedImage(null);
      setImagePreview(null);
    }
  };

  const handlePaste = async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf('image') !== -1) {
        const blob = items[i].getAsFile();
        if (!blob) continue;

        const reader = new FileReader();
        reader.onload = (event) => {
          const base64Image = event.target.result;
          setPastedImage(base64Image);
          setImagePreview(base64Image);
          console.log('Image pasted and stored as base64');
        };
        reader.readAsDataURL(blob);
        e.preventDefault();
        break;
      }
    }
  };

  // Handle drag events
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  // Handle drop event
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      // Check file type
      const file = e.dataTransfer.files[0];
      if (!file.type.match('image.*')) {
        alert('Please upload an image file');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (event) => {
        const base64Image = event.target.result;
        setPastedImage(base64Image);
        setImagePreview(base64Image);
        console.log('Image uploaded and stored as base64');
      };
      reader.readAsDataURL(e.dataTransfer.files[0]);
    } else {
      alert('Please upload an image file');
    }
  };

  // Image download is now handled directly by the shared imageUtils.js utility

  // Handle key press events
  const handleKeyPress = (e) => {
    // Send on Enter (without shift for new line)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  // Audio context for handling browser autoplay policy
  const audioContextRef = useRef(null);
  
  // Function to initialize and resume AudioContext
  const initializeAudioContext = () => {
    try {
      // Create AudioContext if it doesn't exist
      if (!audioContextRef.current) {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        if (AudioContext) {
          audioContextRef.current = new AudioContext();
          console.log('AudioContext created');
        }
      }
      
      // Resume the AudioContext if it's in suspended state
      if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
        audioContextRef.current.resume().then(() => {
          console.log('AudioContext resumed successfully');
        }).catch(error => {
          console.error('Error resuming AudioContext:', error);
        });
      }
    } catch (error) {
      console.error('Error initializing AudioContext:', error);
    }
  };

  // Function to toggle microphone with improved error handling
  const toggleListening = () => {
    try {
      // Initialize audio context on user interaction
      initializeAudioContext();
      
      if (listening) {
        SpeechRecognition.stopListening();
        setIsListeningForPause(false);
      } else {
        resetTranscript();
        // Reset timestamps
        setLastTranscriptUpdate(Date.now());
        setIsListeningForPause(true);
        
        // Start listening with language detection
        const speechLanguage = currentLanguage.split('-')[0]; // Extract base language code
        SpeechRecognition.startListening({ 
          continuous: true,
          language: speechLanguage // Set recognition language to match UI language
        })
          .catch(error => {
            console.error('Speech recognition error:', error);
            setIsListeningForPause(false);
            // Alert user in a more friendly way
            alert(uiLocalization.getLocalizedText('errorMessages', 'microphonePermission', currentLanguage) || 
                  'Please allow microphone permission to use speech recognition');
          });
      }
    } catch (err) {
      console.error('Speech recognition initialization error:', err);
      setIsListeningForPause(false);
    }
  };

  // Determine if voice input is available
  // Always render the mic button if browser supports speech recognition
  // even if mic permission hasn't been granted yet (clicking it will prompt for permission)
  const voiceInputAvailable = browserSupportsSpeechRecognition;

  // Function to handle removing the image
  const handleRemoveImage = () => {
    setPastedImage(null);
    setImagePreview(null);
  };

  // Function to set generate image mode
  const setGenerateImageMode = () => {
    // Focus the input field
    inputRef.current?.focus();
    
    // Add the prefix only if it's not already there
    const imageCommands = uiLocalization.getLocalizedText('generateImageCommand', null, currentLanguage);
    const localizedImageCommands = Array.isArray(imageCommands) ? imageCommands : [imageCommands];
    const preferredCommand =
      localizedImageCommands.find(command => command !== uiLocalization.generateImageCommand) ||
      localizedImageCommands[0] ||
      uiLocalization.generateImageCommand;
    const prefix = `${preferredCommand}: `;
    const hasImageCommandPrefix = localizedImageCommands.some(command => {
      const commandRegex = new RegExp(`^${escapeRegExp(command)}\\s*[:\\uff1a]?\\s*`, 'i');
      return commandRegex.test(input.trim());
    });

    if (!hasImageCommandPrefix) {
      // If there's already text, add it after the prefix
      const currentInput = input.trim();
      
      if (currentInput) {
        setInput(`${prefix}${currentInput}`);
      } else {
        setInput(prefix);
      }
    }
  };

  /**
   * Handle language change button click to toggle menu
   */
  const handleLanguageButtonClick = () => {
    setShowLanguageMenu(prev => !prev);
  };

  /**
   * Handle selecting a language from the dropdown
   */
  const handleLanguageSelect = (languageCode) => {
    setCurrentLanguage(languageCode);
    changeLanguage(languageCode);
    // Call the onLanguageChange prop if provided
    if (onLanguageChange) {
      onLanguageChange(languageCode);
    }
    setShowLanguageMenu(false);
  };

  /**
   * Close language menu when clicking outside
   */
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (languageMenuRef.current && !languageMenuRef.current.contains(event.target)) {
        setShowLanguageMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div 
      className={styles['chat-inner-container']}
      onDragEnter={handleDrag}
    >
      <div className={styles['chat-messages']}>
        {messages.length === 0 ? (
          // Display enhanced onboarding message with localized welcome text
          <div className={styles.message + ' ' + styles.ai}>
            <FontAwesomeIcon 
              icon={faRobot} 
              className={styles['message-icon']}
            />
            <div className={styles['message-content']}>
              {/* Get the welcome message in the current language */}
              <div className={styles.welcomeMessage}>
                <h3>{uiLocalization.welcomeMessages[currentLanguage]?.title || uiLocalization.welcomeMessages.en.title}</h3>
                <p>{uiLocalization.welcomeMessages[currentLanguage]?.description || uiLocalization.welcomeMessages.en.description}</p>
                
                <ul className={styles.featuresList}>
                  {(uiLocalization.welcomeMessages[currentLanguage]?.features || uiLocalization.welcomeMessages.en.features).map((feature, index) => (
                    <li key={index}>{feature}</li>
                  ))}
                </ul>
                
                <p className={styles.examplePrompt}>
                  <em>{uiLocalization.welcomeMessages[currentLanguage]?.tryPrompt || uiLocalization.welcomeMessages.en.tryPrompt}</em>
                </p>
                
                <p className={styles.languageHint}>
                  <small>{uiLocalization.welcomeMessages[currentLanguage]?.langNote || uiLocalization.welcomeMessages.en.langNote}</small>
                </p>
              </div>
            </div>
          </div>
        ) : (
          messages.map((message, index) => (
            <div 
              key={index} 
              className={`${styles.message} ${styles[message.sender]}`}
            >
              <FontAwesomeIcon 
                icon={message.sender === 'user' ? faChild : faRobot} 
                className={styles['message-icon']}
              />
              <div className={styles['message-content']}>
                {message.text && (
                  <ReactMarkdown>{message.text}</ReactMarkdown>
                )}
                
                {/* Show image preview (for user messages with uploaded images) */}
                {message.sender === 'user' && message.imageBase64 && (
                  <div className={styles['image-container']}>
                    <img src={message.imageBase64} alt="Uploaded" className={styles['chat-image']} />
                  </div>
                )}
                
                {/* Show image from AI message */}
                {message.sender === 'ai' && message.imageBase64 && (
                  <div className={styles['image-container']}>
                    <img src={message.imageBase64} alt="Generated" className={styles['chat-image']} />
                    <button 
                      className={styles['save-image-button']}
                      onClick={() => {
                        // Extract the prompt text from the AI message for the filename
                        // The format is usually "Generated image: [prompt text]"
                        let promptText = message.text || '';
                        // Extract just the prompt part if it follows our standard format
                        const match = promptText.match(/Generated image: (.+?)(\.\.\.)?$/i);
                        if (match && match[1]) {
                          promptText = match[1].trim();
                        }
                        // Download with the prompt as the filename
                        downloadImage(message.imageBase64, true, promptText);
                      }}
                    >
                      <FontAwesomeIcon icon={faDownload} /> {uiLocalization.getLocalizedText('buttonText', 'save', currentLanguage)}
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
        
        {/* Loading indicator */}
        {isLoading && (
          <div className={styles['loading-indicator']}>
            <div className={styles['loading-dot']}></div>
            <div className={styles['loading-dot']}></div>
            <div className={styles['loading-dot']}></div>
          </div>
        )}
      </div>
      
      {/* Drag & drop overlay */}
      {dragActive && (
        <div 
          className={styles['drop-overlay']}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <div className={styles['drop-message']}>
            <FontAwesomeIcon icon={faUpload} className={styles['upload-icon']} />
            <p>Drop image here</p>
          </div>
        </div>
      )}

      <div className={styles['input-area']}>
        {imagePreview && (
          <div className={styles['image-preview-container']}>
            <img src={imagePreview} alt="Preview" className={styles['image-preview']} />
            <button 
              onClick={handleRemoveImage}
              className={styles['remove-image-button']}
              aria-label="Remove image"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        )}

        <div 
          ref={dropAreaRef} 
          className={`${styles['drop-area']} ${!imagePreview ? styles['show-drop-area'] : ''}`}
        >
          {uiLocalization.getLocalizedText('dropAreaText', null, currentLanguage)}
        </div>

        <form onSubmit={handleSubmit} className={styles['input-form']}> 
          {/* Text Input Area */} 
          <textarea
            ref={inputRef}
            rows="3"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            onPaste={handlePaste}
            placeholder={uiLocalization.getLocalizedText('inputPlaceholder', null, currentLanguage)}
            disabled={isLoading}
            className={styles['input-field']}
            onDragEnter={handleDrag}
          />
        </form>
        
        {/* Control buttons - below the form and aligned to the left */}
        <div className={styles['button-container-left']}>
          {/* Create Image Button */}
          <button 
            type="button"
            onClick={setGenerateImageMode}
            className={`${styles.button} ${styles['image-button-small']}`}
            disabled={isLoading}
          >
            <FontAwesomeIcon icon={faImage} /> {uiLocalization.getLocalizedText('buttonText', 'createImage', currentLanguage)}
          </button>
          
          {/* Microphone Button */} 
          {voiceInputAvailable && (
            <button 
              type="button"
              onClick={toggleListening}
              className={`${styles.button} ${styles['mic-button-small']} ${listening ? styles['listening'] : ''}`}
              disabled={isLoading}
              title={listening ? uiLocalization.getLocalizedText('buttonText', 'stopListening', currentLanguage) : uiLocalization.getLocalizedText('buttonText', 'startListening', currentLanguage)}
            >
              <FontAwesomeIcon icon={faMicrophone} /> {uiLocalization.getLocalizedText('buttonText', 'askQuestion', currentLanguage)}
            </button>
          )}
          
          {/* Language Change Button with Dropdown */}
          <div className={styles['language-selector-container']} ref={languageMenuRef}>
            <button 
              type="button"
              onClick={handleLanguageButtonClick}
              className={`${styles.button} ${styles['language-button-small']}`}
              disabled={isLoading}
              title={uiLocalization.getLocalizedText('buttonText', 'changeLanguage', currentLanguage)}
            >
              <FontAwesomeIcon icon={faGlobe} /> {uiLocalization.getLocalizedText('buttonText', 'changeLanguage', currentLanguage)} <FontAwesomeIcon icon={faChevronDown} size="xs" />
            </button>
            
            {/* Language Dropdown Menu */}
            {showLanguageMenu && (
              <div className={styles['language-dropdown']}>
                {languageService.getSupportedLanguages().map(lang => (
                  <button
                    key={lang.code}
                    type="button"
                    className={`${styles['language-option']} ${currentLanguage === lang.code ? styles['active'] : ''}`}
                    onClick={() => handleLanguageSelect(lang.code)}
                  >
                    {lang.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Chat;
